/**
 * Bar Chart Visualization Component (DASH-8.2)
 *
 * Renders dynamic widget data as a vertical bar chart using Chart.js.
 * Migrated from Recharts to Chart.js for visual consistency with legacy widgets.
 * Wrapped in React.memo for performance (DASH-8.7)
 */

import React, { useMemo, useCallback, useRef } from 'react';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { useTheme } from '@/contexts/ThemeContext';
import { generateHoverColor } from '@/utils/chartColorUtils';
import type { DynamicWidgetConfig, QueryResult } from '@/types/dashboard';
import type { VisualizationClickEvent } from './DynamicVisualization';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface BarChartVisualizationProps {
  data: QueryResult;
  config: DynamicWidgetConfig;
  /** Callback when a bar is clicked (DASH-24.2) */
  onSegmentClick?: (event: VisualizationClickEvent) => void;
}

/**
 * Bar Chart Visualization (DASH-8.2, DASH-24.2)
 * Shows comparison with groupBy, sort, and axis labels
 * Wrapped in React.memo for performance (DASH-8.7)
 * DASH-24.2: Added click handler for bar drill-down
 */
export const BarChartVisualization = React.memo(function BarChartVisualization({ data, config, onSegmentClick }: BarChartVisualizationProps) {
  const { currentTheme } = useTheme();
  const chartRef = useRef<ChartJS<'bar'>>(null);

  // Use theme primary color for bars
  const primaryColor = currentTheme.colors.primary;
  const hoverColor = generateHoverColor(primaryColor, 0.85);

  // Expect data format: [{ label, value }, ...]
  const chartData = Array.isArray(data.data) ? data.data : [];

  // Apply sorting if configured
  const sortedData = useMemo(() => {
    if (config.visualization.sortBy && config.visualization.sortOrder) {
      const sortField = config.visualization.sortBy;
      const order = config.visualization.sortOrder;

      return [...chartData].sort((a, b) => {
        const aVal = a[sortField] || a.value;
        const bVal = b[sortField] || b.value;

        if (order === 'asc') {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      });
    }
    return chartData;
  }, [chartData, config.visualization.sortBy, config.visualization.sortOrder]);

  // Calculate total for percentages
  const total = useMemo(() => {
    return sortedData.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [sortedData]);

  // Format percentage helper
  const formatPercentage = (value: number): string => {
    if (total === 0) return '0%';
    const percentage = (value / total) * 100;
    return `${percentage.toFixed(1)}%`;
  };

  // Handle chart click (DASH-24.2)
  const handleChartClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (!chartRef.current || !onSegmentClick) return;

      const elements = getElementAtEvent(chartRef.current, event);
      if (elements.length === 0) return;

      const clickedIndex = elements[0].index;
      const clickedItem = sortedData[clickedIndex];

      if (clickedItem) {
        onSegmentClick({
          value: clickedItem.id || clickedItem.label || clickedItem.name,
          index: clickedIndex,
          data: clickedItem,
        });
      }
    },
    [sortedData, onSegmentClick]
  );

  // Prepare Chart.js data
  const chartJsData = {
    labels: sortedData.map((item) => item.label || item.name || 'Unknown'),
    datasets: [
      {
        label: 'Project Count',
        data: sortedData.map((item) => item.value || 0),
        backgroundColor: primaryColor,
        hoverBackgroundColor: hoverColor,
        borderRadius: 4,
        maxBarThickness: 60,
      },
    ],
  };

  // Chart options
  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'x', // Vertical bars
    plugins: {
      legend: {
        display: false, // Hide legend for single dataset
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.parsed.y ?? 0;
            const percentage = formatPercentage(value);
            return `${value} projects (${percentage})`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0, // No decimals for project counts
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false, // Show all labels
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <div className="flex flex-col h-full">
      {/* Bar Chart (DASH-24.2: Added click handler) */}
      <div className="flex-1 cursor-pointer" style={{ minHeight: '250px' }}>
        <Bar ref={chartRef} data={chartJsData} options={options} onClick={handleChartClick} />
      </div>
    </div>
  );
});
